import * as api from "./client";

export type QuestionType =
  | "text"
  | "choice"
  | "dropdown"
  | "range-10"
  | "range-100"
  | "boolean"
  | "testimonial"
  | "multi-testimonial";

export type SubmissionFilters = {
  refereeBusinessType?: string;
  submissionType?: string;
  refereeType?: string;
  region?: string;
  practiceGroup?: string;
  practiceArea?: string;
};

export type DisplayFilterRule = {
  submissionFilters?: SubmissionFilters;
};

export type LocalizedQuestionText = {
  default: string;
} & Record<string, string>;

/** Ordered labels shown along a range scale (not tied to numeric tick values). */
export type RangeLabel = {
  id: string;
  label: LocalizedQuestionText;
  order: number;
};

export function getLocalizedTextDefault(
  text: LocalizedQuestionText | string | null | undefined,
): string {
  if (!text) return "";
  if (typeof text === "string") return text;
  return text.default || text.en || "";
}

export type LibraryQuestion = {
  id: string;
  internalLabel?: string | null;
  text: LocalizedQuestionText;
  type: QuestionType;
  required: boolean;
  defaultValue: string | null;
  description: LocalizedQuestionText | null;
  options: string[] | null;
  /** Present for range-10 / range-100; ordered scale labels. */
  rangeLabels?: RangeLabel[] | null;
  /**
   * Present for `multi-testimonial`; per-locale label shown on the renderer's
   * "+ {label}" add button and the subject field caption.
   */
  subjectLabel?: LocalizedQuestionText | null;
  noExperienceLabel?: LocalizedQuestionText | null;
  displayRules: DisplayFilterRule[];
  isRandom: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateQuestionBody = {
  internalLabel?: string | null;
  text: LocalizedQuestionText;
  type: QuestionType;
  required?: boolean;
  defaultValue?: string | null;
  description?: LocalizedQuestionText | null;
  options?: string[] | null;
  rangeLabels?: RangeLabel[] | null;
  /** Present for `multi-testimonial`; per-locale add-button / subject-field label. */
  subjectLabel?: LocalizedQuestionText | null;
  noExperienceLabel?: LocalizedQuestionText | null;
  displayRules?: DisplayFilterRule[];
};

export type UpdateQuestionBody = CreateQuestionBody;

export type ListQuestionsParams = {
  search?: string;
  type?: QuestionType;
  limit?: number;
  offset?: number;
};

type ListQuestionsResponse = {
  questions: LibraryQuestion[];
  total: number;
  limit: number;
  offset: number;
  requestId: string;
};

type SingleQuestionResponse = {
  question: LibraryQuestion;
  requestId: string;
};

type DeleteQuestionResponse = {
  message: string;
  requestId: string;
};

export const surveyLibrary = {
  list: async (params?: ListQuestionsParams): Promise<ListQuestionsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.type) searchParams.append("type", params.type);
    if (params?.limit !== undefined) searchParams.append("limit", params.limit.toString());
    if (params?.offset !== undefined) searchParams.append("offset", params.offset.toString());

    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return api.get<ListQuestionsResponse>(`/v1/survey/survey-library${query}`);
  },

  getById: async (id: string): Promise<SingleQuestionResponse> => {
    return api.get<SingleQuestionResponse>(`/v1/survey/survey-library/${id}`);
  },

  create: async (data: CreateQuestionBody): Promise<SingleQuestionResponse> => {
    return api.post<SingleQuestionResponse>("/v1/survey/survey-library", data);
  },

  update: async (id: string, data: UpdateQuestionBody): Promise<SingleQuestionResponse> => {
    return api.put<SingleQuestionResponse>(`/v1/survey/survey-library/${id}`, data);
  },

  remove: async (id: string): Promise<DeleteQuestionResponse> => {
    return api.del<DeleteQuestionResponse>(`/v1/survey/survey-library/${id}`);
  },
};

/** Result of paging through the entire survey-library list endpoint. */
export type AllLibraryQuestionsResult = {
  questions: LibraryQuestion[];
  total: number;
};

/** Page size used when loading the full library in one in-memory batch. */
const FETCH_ALL_PAGE_SIZE = 100;

/**
 * Fetch every survey-library question by paging through the list endpoint in
 * large chunks (100 rows) and accumulating the results into a single in-memory
 * array. Guards against a runaway loop by stopping as soon as a page returns no
 * rows or the accumulated offset reaches the reported total.
 */
export async function fetchAllLibraryQuestions(
  params?: Pick<ListQuestionsParams, "search" | "type">,
): Promise<AllLibraryQuestionsResult> {
  const questions: LibraryQuestion[] = [];
  let offset = 0;
  let total = 0;

  do {
    const response = await surveyLibrary.list({
      ...params,
      limit: FETCH_ALL_PAGE_SIZE,
      offset,
    });
    total = response.total;

    // Defensive break: an empty page means there is nothing more to load.
    if (response.questions.length === 0) {
      break;
    }

    questions.push(...response.questions);
    offset += FETCH_ALL_PAGE_SIZE;
  } while (questions.length < total && offset < total);

  return { questions, total };
}
